import { ChangeDetectionStrategy, Component, EventEmitter, Input, Output } from '@angular/core';
import { FormControl } from '@angular/forms';
import * as ui from "../../../models/ui"
import * as model from "../../../models"
import { TicketCreationDTO } from "../../../services/webservice"
import { Subject } from 'rxjs';
import { ValidationService } from '../../../services';

@Component({
	selector: 'ticket-categories-box-office',
	templateUrl: "./ticket-categories-box-office.html",
	changeDetection: ChangeDetectionStrategy.OnPush,
	styles: [require("./ticket-categories-box-office.scss")]
})
export class TicketCategoriesBoxOffice {

	@Output() addTicketItemsToShoppingcart: EventEmitter<TicketCreationDTO[]> = new EventEmitter<TicketCreationDTO[]>();
	@Output() removeTicketItemsFromShoppingcart: EventEmitter<string[]> = new EventEmitter<string[]>();

	@Input() shoppingCartHasErrors: boolean;

	disableControls: boolean = false;

	private _displayCategories: DisplayCategoryWithControls[] = [];
	private _ticketQuota: model.TicketQuota;
	private availability: { [key: string]: number; };
	private formControlChanged: Subject<any> = new Subject<any>();

	@Input() set ticketQuota(ticketQuota: model.TicketQuota) {
		this.availability = {}
		ticketQuota.ticketQuotaStatus.forEach(ticketQuotaStatus => {
			this.availability[ticketQuotaStatus.category.uuid] = ticketQuotaStatus.availableTickets
		});
		this._ticketQuota = ticketQuota
		if(this._displayCategories){
			this.createInputsForDC(this._displayCategories)
		}
	}

	@Input() set displayCategoriesWithDisplayTickets(displayCategoriesWithDisplayTickets: ui.DisplayCategoryWithDisplayTicket[]) {
		this.createInputsForDC(displayCategoriesWithDisplayTickets)
	}

	createInputsForDC(displayCategoriesWithDisplayTickets){
		this._displayCategories = []
		displayCategoriesWithDisplayTickets.forEach(displayCategory => {
			let displayCategoryWithControls: DisplayCategoryWithControls = Object.assign({}, displayCategory);
			displayCategoryWithControls.standardPriceLevelControl = new FormControl(
				"", ValidationService.max(this.availability[displayCategory.category.uuid]))
			if (displayCategoryWithControls.combinedPriceLevel.standard.displayTicket) {
				displayCategoryWithControls.standardPriceLevelControl.setValue(
					displayCategoryWithControls.combinedPriceLevel.standard.displayTicket.numberOfSeats)
				displayCategoryWithControls.standardPriceLevelControl.setValidators(
					ValidationService.max(this.availability[displayCategory.category.uuid] +
						displayCategoryWithControls.combinedPriceLevel.standard.displayTicket.numberOfSeats))
			}
			displayCategoryWithControls.reducedPriceLevelsWithControl = []
			if (displayCategory.combinedPriceLevel) {
				displayCategory.combinedPriceLevel.reduced.forEach(reducedPriceLevel => {
					let reducedPriceLevelWithControl: PriceLevelWithControl = Object.assign({}, reducedPriceLevel);
					reducedPriceLevelWithControl.control = new FormControl("", ValidationService.max(this.availability[displayCategory.category.uuid]))
					if (reducedPriceLevel.displayTicket) {
						reducedPriceLevelWithControl.control.setValue(reducedPriceLevel.displayTicket.numberOfSeats)
						reducedPriceLevelWithControl.control.setValidators(
							ValidationService.max(this.availability[displayCategory.category.uuid] + reducedPriceLevel.displayTicket.numberOfSeats))
					}
					displayCategoryWithControls.reducedPriceLevelsWithControl.push(reducedPriceLevelWithControl)
				});
			}
			this._displayCategories.push(displayCategoryWithControls)
		});
		this.disableControls = false;
	}

	ngOnInit(): void {
		this.formControlChanged
			.debounceTime(750) // wait 0,75 sec after the last event before emitting
			.distinctUntilChanged() // only emit if value is different from previous value
			.subscribe(changes => {
				if (changes.control.valid) {
					this.disableControls = true
					this.addOrRemoveTickets(changes.control.value, changes.displayTicket, changes.plUuid)
				}
			});
	}

	ngOnDestroy(): void {
		this.formControlChanged.unsubscribe();
	}

	private addOrRemoveTickets(amount, displayTicket, plUuid) {
		let add: boolean = false;
		let remove: boolean = false;
		let ticketCreationDTOs: TicketCreationDTO[] = [];
		if (!displayTicket && amount > 0) {
			add = true;
			ticketCreationDTOs = this.createTicketCreationDTO(amount, plUuid)
			this.addTicketItemsToShoppingcart.emit(ticketCreationDTOs);
		} else if (displayTicket && (amount > 0 || !amount || amount === '0')) {
			add = amount - displayTicket.numberOfSeats > 0
			remove = amount - displayTicket.numberOfSeats < 0
			if (add) {
				ticketCreationDTOs = this.createTicketCreationDTO(amount - displayTicket.numberOfSeats, plUuid)
				this.addTicketItemsToShoppingcart.emit(ticketCreationDTOs);
			}
			if (remove) {
				let ticketItemUuidsToBeDeleted: string[] = []
				const numberOfTicketsToRemove: number = displayTicket.numberOfSeats - amount
				for (let i = 0; i < numberOfTicketsToRemove; i++) {
					ticketItemUuidsToBeDeleted.push(displayTicket.ticketItemUuids[i])
				}
				this.removeTicketItemsFromShoppingcart.emit(ticketItemUuidsToBeDeleted);
			}
		}
		if (!add && !remove) {
			this.disableControls = false
		}
	}

	private addValueToFormControlValue(control, value) {
		control.patchValue(Number(control.value) + Number(value))
		control.markAsTouched()
		control.markAsDirty()
		control.updateValueAndValidity();
	}

	private fireChangeEvent(control, displayTicket, plUuid) {
		let changes = {
			control: control,
			displayTicket: displayTicket,
			plUuid: plUuid
		}
		this.formControlChanged.next(changes);
	}

	private createTicketCreationDTO(amount, plUuid): TicketCreationDTO[] {
		let ticketCreationDTOs: TicketCreationDTO[] = [];
		const amountOfTickets =  parseInt(amount, 10);
		if (amountOfTickets) {
			ticketCreationDTOs.push({
				priceLevelUUID: plUuid,
				ticketQuotaUUID: this._ticketQuota.uuid,
				numberOfTickets: amountOfTickets
			})
		}
		return ticketCreationDTOs;
	}

	private createControlMessageText(availability) {
		let text: string = ""
		if (availability === 1) {
			text = "Es ist nur noch 1 Karte verfügbar."
		} else {
			text = "Es sind " + availability + " Karten verfügbar."
		}
		return text
	}
}

interface DisplayCategoryWithControls extends ui.DisplayCategoryWithDisplayTicket {
	standardPriceLevelControl?: FormControl
	reducedPriceLevelsWithControl?: PriceLevelWithControl[]
}

interface PriceLevelWithControl extends model.PriceLevel {
	control?: FormControl
}



// WEBPACK FOOTER //
// ./src/app/components/box-office/ticket-categories-box-office/ticket-categories-box-office.component.ts